// src/components/CommandCenter.jsx
// Expanded hero product mockup — full trading command center.
//
// Features:
//   - 3D cursor parallax (existing)
//   - Boot-up sequence: panels reveal sequentially with a thin green
//     load-line drawing across each (once per browser session)
//   - Count-up KPIs: numbers animate from 0 → target on view
//   - Scroll-scrubbed equity-curve dot: the glowing dot's position
//     along the curve tracks the user's scroll progress
//
// All values are visual UI mock data; no marketing copy.
import { useRef, useEffect, useState } from 'react';
import { useScrollProgress, useBootSequence, useInViewOnce } from '../hooks/useAnimations';

const G = '#00e5a0';
const R = '#ff4d6d';
const B = '#4cc9f0';
const Y = '#ffd166';
const M = '#55556a';

const EQ_PATH =
  'M0,75 L40,68 L80,65 L120,58 L160,62 L200,50 L250,45 L290,48 L330,38 L380,32 L420,35 L470,25 L520,18 L570,12 L600,8';

// KPI data — visual mock values used in the hero dashboard preview
const KPIS = [
  { label: 'Win Rate',      target: 68,    color: G, sub: 'Last 30 days', suffix: '%',  decimals: 0, spark: 'M0,14 L20,12 L40,8 L60,10 L80,5 L100,4' },
  { label: 'ROI',           target: 24.3,  color: G, sub: 'All time',     prefix: '+', suffix: '%', decimals: 1, spark: 'M0,16 L20,14 L40,11 L60,7 L80,5 L100,2' },
  { label: 'ODF Score',     target: 82,    color: G, sub: 'Elite tier',   tail: ' A',  decimals: 0, spark: 'M0,15 L20,13 L40,10 L60,8 L80,5 L100,3' },
  { label: 'Profit Factor', target: 1.84,  color: B, sub: 'Above target', decimals: 2, spark: 'M0,12 L20,10 L40,11 L60,6 L80,7 L100,3' },
];

const TRADES = [
  { p: 'EURUSD', s: 'L', rr: '1.8R', pl: '+$240', c: G, fresh: true },
  { p: 'NQ',     s: 'S', rr: '2.4R', pl: '+$310', c: G },
  { p: 'GOLD',   s: 'L', rr: '0.6R', pl: '-$80',  c: R },
  { p: 'BTC',    s: 'L', rr: '1.2R', pl: '+$150', c: G },
];

/* Boot order — index 0 is browser bar, then each row.
   0: browser bar (always visible)
   1: KPI row
   2: equity + score row
   3: sessions + discipline row
   4: trades + coach row
*/
const TOTAL_BOOT_STEPS = 4;

export default function CommandCenter() {
  const wrapRef    = useRef(null);   // parallax container
  const cardRef    = useRef(null);   // tilted card
  const sectionRef = useRef(null);   // for scroll progress

  // Boot sequence (once per session)
  const bootStep = useBootSequence(TOTAL_BOOT_STEPS, {
    stepDelay: 320,
    initialDelay: 160,
    storageKey: 'odf_cc_boot',
  });

  // Scroll progress for the equity curve dot
  const scrollProgress = useScrollProgress(sectionRef);

  // Heartbeat — periodic "real-time" pulse on the mockup once boot completes
  const [heartbeat, setHeartbeat] = useState(0);
  useEffect(() => {
    if (bootStep < TOTAL_BOOT_STEPS) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setHeartbeat(h => h + 1), 5000);
    return () => clearInterval(id);
  }, [bootStep]);

  // 3D parallax on cursor
  useEffect(() => {
    const wrap = wrapRef.current;
    const card = cardRef.current;
    if (!wrap || !card) return;
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;

    function onMove(e) {
      const r = wrap.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      const ry = (x - 0.5) * 6;
      const rx = -(y - 0.5) * 4;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        card.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;
      });
    }
    function onLeave() {
      cancelAnimationFrame(raf);
      card.style.transform = 'rotateX(0deg) rotateY(0deg)';
    }

    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseleave', onLeave);
    return () => {
      wrap.removeEventListener('mousemove', onMove);
      wrap.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={sectionRef}>
      <div ref={wrapRef} style={{ perspective: '1500px', perspectiveOrigin: '50% 50%' }}>
        <div
          ref={cardRef}
          style={{
            position: 'relative',
            background: 'linear-gradient(180deg, rgba(17,17,24,0.92) 0%, rgba(13,13,20,0.92) 100%)',
            border: '1px solid #1e1e2e',
            borderRadius: 18,
            overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 80px rgba(0,229,160,0.04), inset 0 1px 0 rgba(255,255,255,0.04)',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)',
            willChange: 'transform',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
        >
          {/* Browser bar (always visible) */}
          <div style={{ background: '#0d0d14', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #1e1e2e' }}>
            <div style={{ display: 'flex', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f56' }} />
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffbd2e' }} />
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#27c93f' }} />
            </div>
            <div style={{ flex: 1, background: '#1a1a26', borderRadius: 6, padding: '4px 12px', fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textAlign: 'center' }}>
              odftrades.com/dashboard
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', background: 'rgba(0,229,160,0.10)', border: `1px solid ${G}33`, borderRadius: 5 }}>
              <span style={{ position: 'relative', width: 6, height: 6, borderRadius: '50%', background: G, display: 'inline-block' }}>
                <span style={{ position: 'absolute', inset: -3, borderRadius: '50%', border: `1px solid ${G}`, animation: 'pulse-ring 1.9s ease-out infinite' }} />
                <span key={`hb-${heartbeat}`} style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: `1.5px solid ${G}`, animation: 'heartbeat-burst 1.2s ease-out forwards', opacity: 0 }} />
              </span>
              <span style={{ fontSize: 9, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em' }}>LIVE</span>
            </div>
          </div>

          {/* KPI row */}
          <Panel revealed={bootStep >= 1}>
            <div className="landing-hero-mockup-grid" style={{ padding: 18, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {KPIS.map((c, i) => (
                <KPI key={i} c={c} delay={i * 110} active={bootStep >= 1} />
              ))}
            </div>
          </Panel>

          {/* Row: Equity (1.6fr) + ODF Score (1fr) */}
          <Panel revealed={bootStep >= 2}>
            <div className="hero-row-2col" style={{ padding: '0 18px 14px', display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 10 }}>
              <EquityPanel scrollProgress={scrollProgress} active={bootStep >= 2} />
              <ScoreGauge active={bootStep >= 2} />
            </div>
          </Panel>

          {/* Row: Sessions + Discipline */}
          <Panel revealed={bootStep >= 3}>
            <div className="hero-row-2col" style={{ padding: '0 18px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <SessionsPanel active={bootStep >= 3} />
              <DisciplinePanel />
            </div>
          </Panel>

          {/* Row: Recent Trades + Coach */}
          <Panel revealed={bootStep >= 4}>
            <div className="hero-row-2col" style={{ padding: '0 18px 18px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10 }}>
              <RecentTrades heartbeat={heartbeat} />
              <CoachInsight />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   Helper components
   =========================================================== */

// A row that boots in: opacity + slight translateY + a thin green load-line
function Panel({ revealed, children }) {
  return (
    <div
      style={{
        position: 'relative',
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {/* load-line that sweeps across when this panel becomes active */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 18,
          right: 18,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${G}, transparent)`,
          transform: revealed ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 0.7s cubic-bezier(0.16,1,0.3,1)',
          opacity: 0.65,
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />
      {children}
    </div>
  );
}

// A single KPI tile with count-up + animated sparkline
function KPI({ c, delay, active }) {
  const [ref, inView] = useInViewOnce(0.35);
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!active || !inView) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setVal(c.target); return; }

    const dur = 900;
    let raf = 0;
    let start = 0;
    function frame(now) {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(c.target * eased);
      if (t < 1) raf = requestAnimationFrame(frame);
    }
    const startTimer = setTimeout(() => { raf = requestAnimationFrame(frame); }, delay);
    return () => { clearTimeout(startTimer); if (raf) cancelAnimationFrame(raf); };
  }, [active, inView, c.target, delay]);

  // Format the displayed number
  const decimals = c.decimals ?? 0;
  const num = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();
  const display = `${c.prefix || ''}${num}${c.suffix || ''}${c.tail || ''}`;

  return (
    <div ref={ref} style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '12px 14px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 14, right: 14, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)`, opacity: 0.6 }} />
      <div style={{ fontSize: 10, color: M, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{c.label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: c.color, fontFamily: "'DM Mono',monospace", marginBottom: 2, fontVariantNumeric: 'tabular-nums' }}>{display}</div>
      <div style={{ fontSize: 10, color: M, marginBottom: 6 }}>{c.sub}</div>
      <svg viewBox="0 0 100 18" style={{ width: '100%', height: 14, display: 'block' }}>
        <path
          d={c.spark}
          fill="none"
          stroke={c.color}
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.75"
          strokeDasharray="140"
          strokeDashoffset={active ? '0' : '140'}
          style={{ transition: `stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1) ${0.4 + (delay / 1000)}s` }}
        />
      </svg>
    </div>
  );
}

// Equity panel — animated draw + scroll-scrubbed dot
function EquityPanel({ scrollProgress, active }) {
  // The dot's position along the path tracks scroll progress.
  // We bias toward the centre so it always feels "in motion" even at extremes.
  const dotKeyTime = Math.max(0, Math.min(1, scrollProgress));

  // Animate path stroke offset on activation
  return (
    <div style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.07em' }}>EQUITY CURVE</div>
        <div style={{ fontSize: 10, color: G, fontFamily: "'DM Mono',monospace" }}>+24.3% · 90 DAYS</div>
      </div>
      <svg viewBox="0 0 600 90" style={{ width: '100%', height: 90, display: 'block' }}>
        <defs>
          <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={G} stopOpacity="0.32" />
            <stop offset="100%" stopColor={G} stopOpacity="0" />
          </linearGradient>
          <filter id="eq-glow">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={`${EQ_PATH} L600,90 L0,90 Z`} fill="url(#eq-grad)"
              opacity={active ? 1 : 0}
              style={{ transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1) 0.3s' }} />
        <path
          d={EQ_PATH}
          fill="none"
          stroke={G}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="900"
          strokeDashoffset={active ? '0' : '900'}
          style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(0.16,1,0.3,1) 0.3s' }}
        />
        <DotOnPath progress={dotKeyTime} d={EQ_PATH} />
      </svg>
    </div>
  );
}

// A glowing dot that traces along a path based on a 0..1 progress.
// Uses an animateMotion element + JS to seek to a specific keyTime.
function DotOnPath({ progress, d }) {
  const ref = useRef(null);
  const pathRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !pathRef.current) return;
    const total = pathRef.current.getTotalLength();
    // Clamp progress so the dot never sits exactly at the start
    const p = Math.max(0.02, Math.min(0.98, progress));
    const pt = pathRef.current.getPointAtLength(total * p);
    ref.current.setAttribute('cx', pt.x);
    ref.current.setAttribute('cy', pt.y);
  }, [progress]);

  return (
    <>
      {/* Invisible path used purely for length calculations */}
      <path ref={pathRef} d={d} fill="none" stroke="none" />
      <circle ref={ref} r="4" cx="0" cy="0" fill={G} filter="url(#eq-glow)" />
    </>
  );
}

// ODF Score gauge with count-up
function ScoreGauge({ active }) {
  const [ref, inView] = useInViewOnce(0.4);
  const [score, setScore] = useState(0);
  const [offset, setOffset] = useState(264);

  useEffect(() => {
    if (!active || !inView) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setScore(82); setOffset(47.5); return; }

    const dur = 1300;
    let raf = 0;
    let start = 0;
    function frame(now) {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const s = 82 * eased;
      setScore(s);
      setOffset(264 * (1 - s / 100));
      if (t < 1) raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [active, inView]);

  return (
    <div ref={ref} style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.07em' }}>ODF SCORE</div>
        <div style={{ fontSize: 9, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.12em' }}>ELITE</div>
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 156 }}>
        <svg width="156" height="156" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="#1a1a26" strokeWidth="4.5" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={G} strokeWidth="4.5"
                  strokeDasharray="264" strokeDashoffset={offset}
                  strokeLinecap="round"
                  style={{ filter: `drop-shadow(0 0 6px ${G}55)`, transition: 'stroke-dashoffset 0.05s linear' }} />
        </svg>
        <div style={{ position: 'absolute', textAlign: 'center' }}>
          <div style={{ fontSize: 40, fontWeight: 600, color: '#f0f0f5', fontFamily: "'DM Mono',monospace", lineHeight: 1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(score)}
          </div>
          <div style={{ fontSize: 10, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.14em', marginTop: 6 }}>GRADE A</div>
        </div>
      </div>
    </div>
  );
}

// Sessions panel
function SessionsPanel({ active }) {
  return (
    <div style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
      <div style={{ fontSize: 11, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.07em', marginBottom: 14 }}>SESSION PERFORMANCE</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', height: 54 }}>
        {[['LON', 78, G], ['NY', 65, G], ['ASIA', 38, R]].map((b, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: '100%',
              height: active ? `${b[1]}%` : '0%',
              background: `linear-gradient(180deg, ${b[2]} 0%, ${b[2]}33 100%)`,
              borderRadius: '3px 3px 0 0',
              position: 'relative',
              transition: `height 0.9s cubic-bezier(0.16,1,0.3,1) ${0.2 + i * 0.1}s`,
            }}>
              <span style={{ position: 'absolute', top: -14, left: 0, right: 0, textAlign: 'center', fontSize: 9, color: b[2], fontFamily: "'DM Mono',monospace" }}>{b[1]}%</span>
            </div>
            <span style={{ fontSize: 9, color: M, fontFamily: "'DM Mono',monospace" }}>{b[0]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisciplinePanel() {
  return (
    <div style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
      <div style={{ fontSize: 11, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.07em', marginBottom: 10 }}>DISCIPLINE — LAST 7</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        {[G, G, R, G, G, Y, G].map((c, i) => (
          <div key={i} style={{ width: 22, height: 22, borderRadius: '50%', background: `${c}22`, border: `1px solid ${c}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: M, fontFamily: "'DM Mono',monospace" }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} style={{ width: 22, textAlign: 'center' }}>{d}</span>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: G, fontFamily: "'DM Mono',monospace", textAlign: 'center', letterSpacing: '0.08em' }}>RULE ADHERENCE 86%</div>
    </div>
  );
}

function RecentTrades({ heartbeat = 0 }) {
  // Each heartbeat tick picks a different trade row to pulse
  const pulseIdx = heartbeat % TRADES.length;

  return (
    <div style={{ background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.07em' }}>RECENT TRADES</div>
        <div style={{ fontSize: 10, color: M, fontFamily: "'DM Mono',monospace" }}>14 today</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {TRADES.map((t, i) => {
          const pulsing = heartbeat > 0 && i === pulseIdx;
          const win = t.c === G;
          return (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 10, alignItems: 'center', padding: '5px 8px',
                background: t.fresh ? 'rgba(0,229,160,0.05)' : 'transparent',
                border: `1px solid ${t.fresh ? G + '33' : 'transparent'}`,
                borderRadius: 5, fontSize: 11, fontFamily: "'DM Mono',monospace",
                animation: pulsing ? `${win ? 'trade-pulse' : 'trade-pulse-red'} 1.6s ease-out` : 'none',
              }}
            >
              <span style={{ color: '#f0f0f5' }}>{t.p}</span>
              <span style={{ color: t.s === 'L' ? G : R, fontSize: 9, padding: '1px 6px', background: (t.s === 'L' ? G : R) + '14', border: `1px solid ${(t.s === 'L' ? G : R)}33`, borderRadius: 3, letterSpacing: '0.04em' }}>
                {t.s === 'L' ? 'LONG' : 'SHORT'}
              </span>
              <span style={{ color: M }}>{t.rr}</span>
              <span style={{ color: t.c, fontWeight: 700 }}>{t.pl}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CoachInsight() {
  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(0,229,160,0.07), rgba(76,201,240,0.03))', border: `1px solid ${G}28`, borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: `linear-gradient(90deg, transparent, ${G}, transparent)` }} />
      <div style={{ fontSize: 10, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.12em', marginBottom: 10 }}>COACH INSIGHT</div>
      <div style={{ position: 'relative', paddingLeft: 12 }}>
        <div style={{ position: 'absolute', left: 0, top: 2, bottom: 2, width: 2, background: G, borderRadius: 1 }} />
        <div style={{ height: 5, width: '95%', background: '#1e1e2e', borderRadius: 2, marginBottom: 5, animation: 'coach-shimmer 3.2s ease-in-out infinite' }} />
        <div style={{ height: 5, width: '78%', background: '#1e1e2e', borderRadius: 2, marginBottom: 5, animation: 'coach-shimmer 3.2s 0.3s ease-in-out infinite' }} />
        <div style={{ height: 5, width: '88%', background: '#1e1e2e', borderRadius: 2, marginBottom: 5, animation: 'coach-shimmer 3.2s 0.6s ease-in-out infinite' }} />
        <div style={{ height: 5, width: '62%', background: '#1e1e2e', borderRadius: 2, animation: 'coach-shimmer 3.2s 0.9s ease-in-out infinite' }} />
      </div>
      <div style={{ marginTop: 12, fontSize: 9, color: M, fontFamily: "'DM Mono',monospace", letterSpacing: '0.05em' }}>
        Edge real · Watch Asian session risk
      </div>
    </div>
  );
}
